// Square checkout invocation — thin wrapper around the trusted
// `create-square-checkout` Supabase Edge Function.
//
// Strict wire contract (unchanged):
//   Request body: { "checkout_request_id": "<client-generated UUID>" }
//   No other fields are sent. plan_id, customer_id, pickup_at,
//   trainer_code/id, consent version, meal items, ETM ids, prices,
//   discounts, taxes, totals, Square ids, return/cancel URLs — none
//   of these leave the browser. All are read server-side from
//   authoritative persisted state.
//
// Successful response:
//   { outcome: "checkout_created",
//     order_id: "<uuid>",
//     checkout_url: "<Square hosted checkout URL>",
//     fulfillment: "pickup" }
//
// Idempotency by state fingerprint:
//   A deterministic fingerprint of ALL checkout-relevant authoritative
//   state (selected plan, confirmed pickup, applied trainer code,
//   accepted consent version) is stored alongside the last-minted
//   checkout_request_id. When the fingerprint matches an existing
//   entry, the SAME UUID is reused (network retries, page refreshes,
//   double-clicks are idempotent). When ANY field changes, a fresh
//   UUID is minted so Supabase/Square do not replay a checkout
//   created against older state.

import { supabase, supabaseConfigured } from './supabase';

const STORAGE_KEY = 'etff.checkout_request.v1';

function safeParse(raw) {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function readStore() {
  try {
    return safeParse(sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch { return {}; }
}
function writeStore(next) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
}

function newId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const hex = (n) => Math.floor(Math.random() * 16 ** n).toString(16).padStart(n, '0');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${(8 + Math.floor(Math.random() * 4)).toString(16)}${hex(3)}-${hex(12)}`;
}

// Deterministic fingerprint of the checkout-relevant state.
// Values are the customer's authoritative choices; the actual pricing
// and discounts are server-side. Fingerprint is used only to key the
// idempotency UUID.
export function computeCheckoutFingerprint({
  plan_id, pickup_at, trainer_code, consent_version,
}) {
  const s = (v) => (typeof v === 'string' && v.length ? v : null);
  return JSON.stringify({
    plan_id: s(plan_id),
    pickup_at: s(pickup_at),
    trainer_code: s(trainer_code) ? String(trainer_code).trim().toLowerCase() : null,
    consent_version: s(consent_version),
  });
}

// Return the checkout_request_id to send for the given state.
// Reuses the stored UUID iff the fingerprint matches; otherwise
// mints and stores a fresh UUID.
export function getOrCreateCheckoutRequestId(state) {
  const fingerprint = computeCheckoutFingerprint(state || {});
  const store = readStore();
  if (
    store.fingerprint === fingerprint &&
    typeof store.checkout_request_id === 'string' &&
    store.checkout_request_id.length >= 8
  ) {
    return { id: store.checkout_request_id, rotated: false };
  }
  const id = newId();
  writeStore({
    fingerprint,
    checkout_request_id: id,
    created_at: new Date().toISOString(),
  });
  return { id, rotated: true };
}

// Force a new UUID for the current or given state.
export function rotateCheckoutRequestId(state) {
  const fingerprint = computeCheckoutFingerprint(state || {});
  const id = newId();
  writeStore({
    fingerprint,
    checkout_request_id: id,
    created_at: new Date().toISOString(),
  });
  return id;
}

export function clearCheckoutRequestId() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

function logSanitized(label, err) {
  try {
    console.warn('[etff:checkout]', label, {
      name: err?.name || null,
      code: err?.code || null,
      message: err?.message || null,
      status: err?.status || null,
    });
  } catch { /* ignore */ }
}

// Invoke the Edge Function.
// Returns { ok: true, data } | { ok: false, error, outcome? }.
export async function invokeCreateSquareCheckout(checkoutRequestId) {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, error: "Checkout is temporarily unavailable. Please try again in a moment." };
  }
  if (!checkoutRequestId) {
    return { ok: false, error: "We couldn't start your checkout. Please try again." };
  }
  try {
    const { data, error } = await supabase.functions.invoke('create-square-checkout', {
      body: { checkout_request_id: checkoutRequestId },
    });
    if (error) {
      logSanitized('invoke:error', error);
      return { ok: false, error: mapOutcomeToMessage(error?.context?.outcome) || "We couldn't start your checkout. Please try again." };
    }
    const outcome = typeof data?.outcome === 'string' ? data.outcome : null;
    if (outcome === 'checkout_created' && typeof data?.checkout_url === 'string' && data.checkout_url.length > 0) {
      return { ok: true, data };
    }
    return { ok: false, outcome, error: mapOutcomeToMessage(outcome) };
  } catch (err) {
    logSanitized('invoke:threw', err);
    return { ok: false, error: "We couldn't start your checkout. Please try again." };
  }
}

function mapOutcomeToMessage(outcome) {
  switch (outcome) {
    case 'no_selected_plan':
      return 'Please select a meal plan before continuing to checkout.';
    case 'plan_unavailable':
      return 'One or more meals in your plan are no longer available. Please refresh your plan.';
    case 'pickup_required':
      return 'Please confirm a pickup date and time before continuing.';
    case 'consent_required':
      return 'Please read and accept the terms before continuing.';
    case 'checkout_unavailable':
      return 'Checkout is temporarily unavailable. Please try again in a moment.';
    case 'not_authenticated':
      return 'Please sign in again to continue to checkout.';
    default:
      return null;
  }
}
